import { Icon, Text, Colors, SelectOutlined } from '@amelio/ui-library';
import { useState } from 'react';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';

import { useGetPerformanceScoreLevels } from '@/api/competency-services/performance-score-level';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { LevelIconPreview } from '@/pages/Competencies/components/levels/LevelIconPreview';
import { getTextByLanguage } from '@/utils/localization';

interface PerformanceCardProps {
    label: string;
    value: string;
    overallPerformanceLevel?: PerformanceScoreLevel | null;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const PerformanceCard = ({
    label,
    value,
    overallPerformanceLevel,
    onChange,
    disabled = false,
}: PerformanceCardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const [isEditing, setIsEditing] = useState(false);
    const { data: levels = [] } = useGetPerformanceScoreLevels(!disabled);

    // If disabled (completed), trust the provided snapshot object first so history stays stable.
    const selectedLevel =
        disabled && overallPerformanceLevel
            ? overallPerformanceLevel
            : levels.find((l) => l.id === value);

    const translatedOptions = levels.map((opt) => ({
        value: opt.id as string,
        label: getTextByLanguage(opt.label, language),
        isSelected: opt.id === selectedLevel?.id,
        startIcon: (
            <div className={'flex h-5 w-5 items-center justify-center'}>
                <LevelIconPreview
                    icon={opt.icon}
                    iconColor={opt.color}
                    className={'h-4 w-4'}
                />
            </div>
        ),
    }));

    const handleSelectChange = (newValue: string) => {
        if (disabled) {
            return;
        }
        onChange(newValue as string);
        setIsEditing(false);
    };

    const handleEditClick = () => {
        if (disabled) {
            return;
        }
        setIsEditing(true);
    };

    // No resolved level here means every competency was skipped (Not Observed) — the calculated score
    // deliberately does not fall back to a real level in that case (#15605). Never blank: tell the
    // manager why, in their own language, rather than showing an unexplained dash.
    const selectedLabel = selectedLevel
        ? getTextByLanguage(selectedLevel.label, language)
        : t('postReviewResults.performance.notObserved');

    return (
        <div
            className={
                'group relative flex min-h-[80px] w-full flex-col gap-2 rounded-[8px] border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-[16px] transition-all duration-200 hover:border-purple-300 hover:shadow-md sm:min-h-24 md:min-h-28'
            }
        >
            <div className={'flex h-6 items-center justify-between'}>
                <div className={'flex items-center gap-1.5'}>
                    <Icon
                        name={'school-award'}
                        size={16}
                        color={Colors.SECONDARY_800}
                    />

                    <Text
                        size={'small'}
                        weight={'semibold'}
                        color={Colors.BASE_600}
                        className={'text-[12px] tracking-wide uppercase'}
                    >
                        {label}
                    </Text>
                </div>

                {!isEditing && !disabled && (
                    <button
                        onClick={handleEditClick}
                        className={
                            'focus-visible:ring-primary-800 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                        }
                        aria-label={'Edit performance score'}
                    >
                        <Icon
                            name={'money-icon_pencil'}
                            size={'sm'}
                            className={'!text-violet'}
                        />
                    </button>
                )}
            </div>

            {isEditing ? (
                <div className={'max-w-xs'}>
                    <SelectOutlined
                        value={selectedLevel?.id ?? undefined}
                        onChange={handleSelectChange}
                        options={translatedOptions}
                        placeholder={'Select performance score'}
                        size={'sm'}
                        sort={'none'}
                        className={'!border-secondary/10 !bg-secondary/5 !rounded-lg !ring-0'}
                        textClassName={'!text-violet !font-bold !text-body-sm md:!text-body'}
                        chevronClassName={'!text-violet'}
                    />
                </div>
            ) : (
                <div className={'flex min-h-8 min-w-0 items-start gap-2 ps-1'}>
                    {selectedLevel && (
                        <LevelIconPreview
                            icon={selectedLevel.icon}
                            iconColor={selectedLevel.color}
                            className={'mt-0.5 h-5 w-5 flex-shrink-0'}
                        />
                    )}
                    <Text
                        size={'small'}
                        weight={'semibold'}
                        className={`${selectedLevel ? '!text-violet' : '!text-gray-400 italic'} min-w-0 !font-bold break-words`}
                    >
                        <span style={selectedLevel ? { color: selectedLevel.color } : {}}>
                            {selectedLabel}
                        </span>
                    </Text>
                </div>
            )}
        </div>
    );
};

PerformanceCard.displayName = 'PerformanceCard';
